import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { cmsApiConfig } from '../../cmsConfig';
import {
  ConversionProjectDetails,
  ConversionProjectViewModel,
  ConversionProjectParticipant,
  DecisionsOnPackagesText,
} from '../../types/ConversionProject';
import { IdNamePair } from '../../types/global';
import { StatusForCoachText } from '../../types/Coach/ParticipationListItem';

type ResolveProjectIdentifierResponse =
  | string
  | {
      projectId?: string;
      projectGuid?: string;
      id?: string;
    };

export const conversionProjectApi = createApi({
  reducerPath: 'conversionProjectApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${cmsApiConfig.url}/conversionProject`,
    headers: { 'X-CSRF': '1' },
    credentials: 'include',
  }),
  tagTypes: ['participants'],
  endpoints: (builder) => ({
    getPageTexts: builder.query<ConversionProjectViewModel, void>({
      query: () => {
        return {
          url: '/getPageTexts',
          method: 'GET',
        };
      },
    }),
    resolveProjectIdentifier: builder.query<string, string>({
      query: (projectKey) => ({
        url: `/resolve?projectKey=${encodeURIComponent(projectKey)}`,
        responseHandler: async (response) => {
          const contentType = response.headers.get('content-type') || '';

          if (contentType.includes('application/json')) {
            return response.json();
          }

          return response.text();
        },
      }),
      transformResponse: (response: ResolveProjectIdentifierResponse) => {
        if (typeof response === 'string') {
          const trimmedResponse = response.trim();

          if (
            trimmedResponse.startsWith('{') &&
            trimmedResponse.endsWith('}')
          ) {
            try {
              const parsedResponse = JSON.parse(
                trimmedResponse
              ) as ResolveProjectIdentifierResponse;

              if (typeof parsedResponse === 'string') {
                return parsedResponse;
              }

              return (
                parsedResponse.projectId ||
                parsedResponse.projectGuid ||
                parsedResponse.id ||
                ''
              );
            } catch {
              return trimmedResponse;
            }
          }

          return trimmedResponse;
        }

        return response.projectId || response.projectGuid || response.id || '';
      },
    }),
    getAConversionProjectDetails: builder.query<
      ConversionProjectDetails,
      string
    >({
      query: (id: string) => {
        return {
          url: `/getConversionProjectDetails/${id}`,
          method: 'GET',
        };
      },
    }),
    getNewParticipants: builder.query<ConversionProjectParticipant[], string>({
      query: (id: string) => {
        return {
          url: `/getNewParticipants/${id}`,
          method: 'GET',
        };
      },
      transformResponse: (response: ConversionProjectParticipant[]) => {
        return response.map((item) => {
          const decisionOnPackageText = item.decisionOnPackage?.map(
            (decision) => {
              return DecisionsOnPackagesText[
                decision as keyof typeof DecisionsOnPackagesText
              ];
            }
          );
          return {
            ...item,
            statusForCoachText:
              StatusForCoachText[
                item.statusForCoach as keyof typeof StatusForCoachText
              ],
            decisionOnPackageText: decisionOnPackageText?.join(', '),
          } as ConversionProjectParticipant;
        });
      },
      providesTags: [{ type: 'participants', id: 'new' }],
    }),
    getOngoingParticipants: builder.query<
      ConversionProjectParticipant[],
      string
    >({
      query: (id: string) => {
        return {
          url: `/getOngoingParticipants/${id}`,
          method: 'GET',
        };
      },
      transformResponse: (response: ConversionProjectParticipant[]) => {
        return response.map((item) => {
          const decisionOnPackageText = item.decisionOnPackage?.map(
            (decision) => {
              return DecisionsOnPackagesText[
                decision as keyof typeof DecisionsOnPackagesText
              ];
            }
          );
          return {
            ...item,
            statusForCoachText:
              StatusForCoachText[
                item.statusForCoach as keyof typeof StatusForCoachText
              ],
            decisionOnPackageText: decisionOnPackageText?.join(', '),
          } as ConversionProjectParticipant;
        });
      },
      providesTags: [{ type: 'participants', id: 'ongoing' }],
    }),
    getFinishedParticipants: builder.query<
      ConversionProjectParticipant[],
      string
    >({
      query: (id: string) => {
        return {
          url: `/getFinishedParticipants/${id}`,
          method: 'GET',
        };
      },
      transformResponse: (response: ConversionProjectParticipant[]) => {
        return response.map((item) => {
          const decisionOnPackageText = item.decisionOnPackage?.map(
            (decision) => {
              return DecisionsOnPackagesText[
                decision as keyof typeof DecisionsOnPackagesText
              ];
            }
          );
          return {
            ...item,
            statusForCoachText:
              StatusForCoachText[
                item.statusForCoach as keyof typeof StatusForCoachText
              ],
            decisionOnPackageText: decisionOnPackageText?.join(', '),
          } as ConversionProjectParticipant;
        });
      },
      providesTags: [{ type: 'participants', id: 'finished' }],
    }),
    getCoaches: builder.query<IdNamePair[], void>({
      query: () => {
        return {
          url: `/getCoaches`,
          method: 'GET',
        };
      },
    }),
    assignCoach: builder.mutation<
      void,
      {
        participantId: string;
        coachId: string;
        tagToInvalidate: 'new' | 'ongoing' | 'finished';
        projectId: string;
      }
    >({
      query: ({ participantId, coachId }) => {
        return {
          url: `/assignCoach?participantId=${participantId}&coachId=${coachId}`,
          method: 'PUT',
        };
      },
    }),
  }),
});

export const {
  useLazyGetPageTextsQuery,
  useLazyGetAConversionProjectDetailsQuery,
  useLazyGetNewParticipantsQuery,
  useLazyGetOngoingParticipantsQuery,
  useLazyGetFinishedParticipantsQuery,
  useLazyGetCoachesQuery,
  useAssignCoachMutation,
  useResolveProjectIdentifierQuery,
} = conversionProjectApi;
